import React from 'react'
import { HelpCircle, History, ChevronDown, ChevronUp, PlayCircle, Pencil } from 'lucide-react'
import ClientDashboardV3ScoreGauge from '../ClientDashboardV3ScoreGauge'
import PerformanceDashboardService from '../../../../services/dashboards/PerformanceDashboard.service'
import ReportService from '../../../../services/reports/Report.service'
import ClientDataService from '../../../../services/ClientData.service'
import CurrentClientProvider from '../../../../providers/CurrentClientProvider'
import EpochMonthService from '../../../../services/EpochMonth.service'
import { VERTICAL_BENCHMARKS, resolveClientVertical } from '../../../../constants/healthScoreBenchmarks'
import { computeMetricInsight } from '../../../../constants/healthInsightsLogic'
import { HealthScoreDrawer } from './HealthScoreDrawer'
import { VideoPlayerModal } from './VideoPlayerModal'
import { cn } from '../../../../lib/utils'
import KpiHelpIcon, { type KpiTooltipKey } from './KpiHelpIcon'

export interface HealthCashFlowApiData {
  salesTY: number;
  markdownsTY: number;
  cashMarginTY: number;
  inflowCostTY: number;
  inflowRetailTY: number;
  grossMarginTY: number;
  salesTC: number;
  markdownsTC: number;
  cashMarginTC: number;
  inflowCostTC: number;
  inflowRetailTC: number;
  grossMarginTC: number;
  invAvgCostThisYear: number;
  invAvgCostComp: number;
  invAvgRetailThisYear: number;
  invAvgRetailComp: number;
  inventoryCurrentThisYear: number;
  inventoryCurrentComp: number;
}

export interface HealthCashFlowMetrics {
  sales: number;
  salesComp: number;
  markdowns: number;
  markdownsComp: number;
  markdownPct: number;
  markdownPctComp: number;
  cashMargin: number;
  cashMarginComp: number;
  cashMarginPct: number;
  cashMarginPctComp: number;
  inflowCost: number;
  inflowCostComp: number;
  costOfPurchasesPct: number;
  costOfPurchasesPctComp: number;
  grossMargin: number;
  grossMarginComp: number;
  grossMarginPct: number;
  grossMarginPctComp: number;
  gmroi: number;
  gmroiComp: number;
  cmroi: number;
  cmroiComp: number;
  invAvgCost: number;
  invAvgCostComp: number;
  turn: number;
  turnComp: number;
  freshness: number;
  freshnessComp: number;
}

export function deriveHealthCashFlowMetrics(data: HealthCashFlowApiData | null): HealthCashFlowMetrics | null {
  if (!data) return null

  const sales = data.salesTY || 0
  const salesComp = data.salesTC || 0
  const markdowns = data.markdownsTY || 0
  const markdownsComp = data.markdownsTC || 0
  const cashMargin = data.cashMarginTY || 0
  const cashMarginComp = data.cashMarginTC || 0
  const inflowCost = data.inflowCostTY || 0
  const inflowCostComp = data.inflowCostTC || 0
  const inflowRetail = data.inflowRetailTY || 0
  const inflowRetailComp = data.inflowRetailTC || 0
  const grossMargin = data.grossMarginTY || 0
  const grossMarginComp = data.grossMarginTC || 0
  const invAvgCost = data.invAvgCostThisYear || 0
  const invAvgCostComp = data.invAvgCostComp || 0
  const invAvgRetail = data.invAvgRetailThisYear || 0
  const invAvgRetailComp = data.invAvgRetailComp || 0
  const inventoryCurrent = data.inventoryCurrentThisYear || 0
  const inventoryCurrentComp = data.inventoryCurrentComp || 0

  return {
    sales,
    salesComp,
    markdowns,
    markdownsComp,
    markdownPct: sales ? markdowns / sales : 0,
    markdownPctComp: salesComp ? markdownsComp / salesComp : 0,
    cashMargin,
    cashMarginComp,
    cashMarginPct: sales ? (cashMargin / sales) * 100 : 0,
    cashMarginPctComp: salesComp ? (cashMarginComp / salesComp) * 100 : 0,
    inflowCost,
    inflowCostComp,
    costOfPurchasesPct: ReportService.calculateCop({ inflowCost, sales }) * 100,
    costOfPurchasesPctComp: ReportService.calculateCop({ inflowCost: inflowCostComp, sales: salesComp }) * 100,
    grossMargin,
    grossMarginComp,
    grossMarginPct: sales ? (grossMargin / sales) * 100 : 0,
    grossMarginPctComp: salesComp ? (grossMarginComp / salesComp) * 100 : 0,
    gmroi: invAvgCost ? grossMargin / invAvgCost : 0,
    gmroiComp: invAvgCostComp ? grossMarginComp / invAvgCostComp : 0,
    cmroi: invAvgCost ? cashMargin / invAvgCost : 0,
    cmroiComp: invAvgCostComp ? cashMarginComp / invAvgCostComp : 0,
    invAvgCost,
    invAvgCostComp,
    turn: ReportService.calculateTurn({ sales, invAvg: invAvgRetail, format: false }) as number,
    turnComp: ReportService.calculateTurn({ sales: salesComp, invAvg: invAvgRetailComp, format: false }) as number,
    freshness: ReportService.calculateFreshness({ inflowRetail, inventory: inventoryCurrent, format: false }) as number,
    freshnessComp: ReportService.calculateFreshness({ inflowRetail: inflowRetailComp, inventory: inventoryCurrentComp, format: false }) as number,
  }
}

export type ScoreTone = 'excellent' | 'good' | 'average' | 'poor' | 'fair'

export type HealthMetricKey = 'salesGrowth' | 'turn' | 'markdowns' | 'freshness' | 'grossMargin' | 'cashMargin'

export interface HealthSnapshotRow {
  label: string;
  badge: string;
  tone: ScoreTone;
  filled: number;
  caption: string;
  metricKey: HealthMetricKey;
}

export interface HealthSnapshotSummary {
  score: number;
  compareLabel: string;
  rows: HealthSnapshotRow[];
}

export interface CashFlowSummaryRow {
  label: string;
  dotColor: string;
  value: string;
  sub: string;
  delta?: string;
  positive?: boolean;
  strong?: boolean;
}

export interface RoiSummaryCard {
  label: string;
  value: string;
  helper: string;
  delta: string;
  positive: boolean;
}

const scoreBadgeStyles: Record<ScoreTone, { bg: string; text: string }> = {
  excellent: { bg: '#17B26A', text: '#FFFFFF' },
  good: { bg: '#ECFDF3', text: '#027A48' },
  average: { bg: '#FEFBE8', text: '#A16207' },
  fair: { bg: '#FEF6EE', text: '#B54708' },
  poor: { bg: '#FEF3F2', text: '#B42318' },
}

const EXCELLENT_BAR_COLORS = ['#C0DD97', '#97C459', '#639922', '#3B6D11', '#27500A']
const TONE_BAR_COLORS: Record<ScoreTone, string> = {
  excellent: '#16A34A',
  good: '#65A30D',
  average: '#D97706',
  fair: '#EA580C',
  poor: '#DC2626',
}

function getSegmentColor(tone: ScoreTone, index: number, filled: number): string {
  if (index >= filled) return '#E5E7EB'
  if (tone === 'excellent' || tone === 'good') {
    return EXCELLENT_BAR_COLORS[index] ?? '#16A34A'
  }
  return TONE_BAR_COLORS[tone] ?? '#E5E7EB'
}

function HelpDot({ tooltipKey }: { tooltipKey?: KpiTooltipKey }) {
  if (!tooltipKey) {
    return <HelpCircle className="h-3 w-3 text-gray-400" />
  }

  return <KpiHelpIcon tooltipKey={tooltipKey} />
}

function resolveHealthRowTooltipKey(label: string): KpiTooltipKey | null {
  switch (label) {
    case 'Gross Margin':
      return 'grossMargin'
    case 'Markdowns':
      return 'markdowns'
    case 'Annual Turn':
      return 'annualTurn'
    default:
      return null
  }
}

function resolveCashFlowTooltipKey(label: string): KpiTooltipKey | null {
  switch (label) {
    case 'Cash Margin':
      return 'cashMargin'
    case 'Gross Margin':
      return 'grossMargin'
    case 'Markdowns':
      return 'markdowns'
    default:
      return null
  }
}

function DeltaBadge({ delta, invert = false }: { delta: number; invert?: boolean }) {
  const positive = delta >= 0
  const good = invert ? !positive : positive
  return (
    <span className={cn('inline-flex items-center gap-1 text-xs font-semibold', good ? 'text-emerald-600' : 'text-red-600')}>
      <span className="text-[10px] leading-none">{positive ? '↗' : '↘'}</span>
      {positive ? '+' : ''}{delta.toFixed(1)}
    </span>
  )
}

function formatSignedPercent(value: number) {
  const prefix = value >= 0 ? '+' : ''
  return `${prefix}${ReportService.formatPercent(value)}`
}

function formatSignedPointDelta(value: number) {
  const prefix = value >= 0 ? '+' : ''
  return `${prefix}${ReportService.formatRatio(value)} pts`
}

function formatSignedRatio(value: number) {
  const prefix = value >= 0 ? '+' : ''
  return `${prefix}${ReportService.formatRatio(value)}`
}

function percentageDelta(current: number, compare: number) {
  if (!compare) return 0
  return ((current - compare) / Math.abs(compare)) * 100
}

function toScoreTone(labelValue: number, thresholds: { excellent: number; good: number; average: number; fair: number }, lowerIsBetter = false): ScoreTone {
  if (lowerIsBetter) {
    if (labelValue <= thresholds.excellent) return 'excellent'
    if (labelValue <= thresholds.good) return 'good'
    if (labelValue <= thresholds.average) return 'average'
    if (labelValue <= thresholds.fair) return 'fair'
    return 'poor'
  }

  if (labelValue >= thresholds.excellent) return 'excellent'
  if (labelValue >= thresholds.good) return 'good'
  if (labelValue >= thresholds.average) return 'average'
  if (labelValue >= thresholds.fair) return 'fair'
  return 'poor'
}

function badgeForTone(tone: ScoreTone) {
  switch (tone) {
    case 'excellent': return 'Excellent'
    case 'good': return 'Good'
    case 'average': return 'Average'
    case 'fair': return 'Fair'
    case 'poor':
    default: return 'Poor'
  }
}

function filledForTone(tone: ScoreTone) {
  switch (tone) {
    case 'excellent': return 5
    case 'good': return 4
    case 'average': return 3
    case 'fair': return 2
    case 'poor':
    default: return 1
  }
}

export function buildHealthSnapshot(summary: HealthCashFlowMetrics | null): HealthSnapshotSummary | null {
  if (!summary) return null

  const salesGrowth = percentageDelta(summary.sales, summary.salesComp)
  const rows: HealthSnapshotRow[] = [
    {
      label: 'Gross Margin',
      metricKey: 'grossMargin' as HealthMetricKey,
      tone: toScoreTone(summary.grossMarginPct, { excellent: 45, good: 38, average: 32, fair: 26 }),
      badge: '',
      filled: 0,
      caption: `${ReportService.formatPercent(summary.grossMarginPct)} with ${formatSignedPointDelta(summary.grossMarginPct - summary.grossMarginPctComp)} vs LY.`,
    },
    {
      label: 'Sales Growth',
      metricKey: 'salesGrowth' as HealthMetricKey,
      tone: toScoreTone(salesGrowth, { excellent: 12, good: 5, average: 0, fair: -5 }),
      badge: '',
      filled: 0,
      caption: `${formatSignedPercent(salesGrowth)} vs LY revenue for the loaded KPI window.`,
    },
    {
      label: 'Markdowns',
      metricKey: 'markdowns' as HealthMetricKey,
      tone: toScoreTone(summary.markdownPct * 100, { excellent: 6, good: 10, average: 14, fair: 18 }, true),
      badge: '',
      filled: 0,
      caption: `${ReportService.formatPercent(summary.markdownPct * 100)} of sales with ${formatSignedPointDelta((summary.markdownPct - summary.markdownPctComp) * 100)} vs LY.`,
    },
    {
      label: 'Annual Turn',
      metricKey: 'turn' as HealthMetricKey,
      tone: toScoreTone(summary.turn, { excellent: 3.5, good: 2.5, average: 1.8, fair: 1.2 }),
      badge: '',
      filled: 0,
      caption: `${ReportService.formatRatio(summary.turn)}× versus ${ReportService.formatRatio(summary.turnComp)}× in the comparison window.`,
    },
    {
      label: 'Inventory Freshness',
      metricKey: 'freshness' as HealthMetricKey,
      tone: toScoreTone(summary.freshness, { excellent: 40, good: 28, average: 18, fair: 10 }),
      badge: '',
      filled: 0,
      caption: `${ReportService.formatPercent(summary.freshness)} with ${formatSignedPointDelta(summary.freshness - summary.freshnessComp)} vs LY.`,
    },
  ].map((row) => ({
    ...row,
    badge: badgeForTone(row.tone),
    filled: filledForTone(row.tone),
  }))

  const score = Math.round((rows.reduce((acc, row) => acc + row.filled, 0) / (rows.length * 5)) * 100)

  return {
    score,
    compareLabel: 'Compare baseline: last year',
    rows,
  }
}

export function buildCashFlowRows(summary: HealthCashFlowMetrics | null): CashFlowSummaryRow[] {
  if (!summary) return []

  return [
    {
      label: 'Revenue',
      dotColor: '#374151',
      value: PerformanceDashboardService.moneyFormat(summary.sales, true),
      sub: `LY ${PerformanceDashboardService.moneyFormat(summary.salesComp, true)}`,
      delta: formatSignedPercent(percentageDelta(summary.sales, summary.salesComp)),
      positive: summary.sales >= summary.salesComp,
    },
    {
      label: 'Purchases',
      dotColor: '#7F22FE',
      value: PerformanceDashboardService.moneyFormat(summary.inflowCost, true),
      sub: `${ReportService.formatPercent(summary.costOfPurchasesPct)} of revenue`,
      delta: formatSignedPointDelta(summary.costOfPurchasesPct - summary.costOfPurchasesPctComp),
      positive: summary.costOfPurchasesPct <= summary.costOfPurchasesPctComp,
    },
    {
      label: 'Cash Margin',
      dotColor: '#374151',
      value: PerformanceDashboardService.moneyFormat(summary.cashMargin, true),
      sub: `${ReportService.formatPercent(summary.cashMarginPct)} of revenue`,
      delta: formatSignedPointDelta(summary.cashMarginPct - summary.cashMarginPctComp),
      positive: summary.cashMarginPct >= summary.cashMarginPctComp,
      strong: true,
    },
    {
      label: 'Gross Margin',
      dotColor: '#FFB900',
      value: PerformanceDashboardService.moneyFormat(summary.grossMargin, true),
      sub: `${ReportService.formatPercent(summary.grossMarginPct)} of revenue`,
      delta: formatSignedPointDelta(summary.grossMarginPct - summary.grossMarginPctComp),
      positive: summary.grossMarginPct >= summary.grossMarginPctComp,
    },
    {
      label: 'Markdowns',
      dotColor: '#00C950',
      value: PerformanceDashboardService.moneyFormat(summary.markdowns, true),
      sub: `${ReportService.formatPercent(summary.markdownPct * 100)} of revenue`,
      delta: formatSignedPointDelta((summary.markdownPct - summary.markdownPctComp) * 100),
      positive: summary.markdownPct <= summary.markdownPctComp,
    },
  ]
}

/**
 * Calculates CMROI/GMROI using the same ReportService formulas as the Inventory Performance
 * report. Callers are responsible for supplying an `invAvgCost` that is weighted the same way
 * the report weights it (per-row/class cost-complement applied to that row's average inventory,
 * then summed) rather than a single blended cost-complement applied to the client total average
 * inventory, otherwise the results will diverge from the report when margins vary by class.
 */
export function calculateRoiMetrics({ sales, inflowCost, markdowns, costComplement, imu, invAvgCost }: { sales: number; inflowCost: number; markdowns: number; costComplement: number; imu: number; invAvgCost: number }): { cmroi: number; gmroi: number } {
  return {
    cmroi: ReportService.calculateCmroi({ sales, inflowCost, invAvgCost, format: false }) as number,
    gmroi: ReportService.calculateGmroi({ sales, markdowns, costComplement, imu, invAvgCost, format: false }) as number,
  }
}

export function buildRoiSummaryCards(summary: HealthCashFlowMetrics | null): RoiSummaryCard[] {
  if (!summary) return []

  return [
    {
      label: 'GMROI',
      value: `${ReportService.formatRatio(summary.gmroi)}×`,
      helper: `LY ${ReportService.formatRatio(summary.gmroiComp)}×`,
      delta: formatSignedRatio(summary.gmroi - summary.gmroiComp),
      positive: summary.gmroi >= summary.gmroiComp,
    },
    {
      label: 'CMROI',
      value: `${ReportService.formatRatio(summary.cmroi)}×`,
      helper: `LY ${ReportService.formatRatio(summary.cmroiComp)}×`,
      delta: formatSignedRatio(summary.cmroi - summary.cmroiComp),
      positive: summary.cmroi >= summary.cmroiComp,
    },
  ]
}

function CashDonut() {
  return (
    <svg viewBox="0 0 173 173" className="h-full w-full" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M86.5 0C104.767 0 122.565 5.78295 137.343 16.52L120.873 39.1896C110.882 31.9307 98.8495 28.0211 86.5 28.0211V0Z" fill="#7C3AED" />
      <path d="M137.343 16.52C150.249 25.8967 160.311 38.6597 166.415 53.3979C172.52 68.1361 174.43 84.2754 171.935 100.032C169.439 115.788 162.635 130.547 152.275 142.677C141.915 154.808 128.402 163.837 113.23 168.766L104.571 142.117C114.828 138.784 123.963 132.68 130.968 124.479C137.972 116.278 142.572 106.3 144.259 95.6481C145.946 84.9961 144.654 74.085 140.527 64.1211C136.4 54.1572 129.598 45.5287 120.873 39.1896L137.343 16.52Z" fill="#F59E0B" />
      <path d="M113.23 168.766C101.338 172.63 88.7501 173.87 76.333 172.4C63.9159 170.931 51.9651 166.787 41.3039 160.253C30.6427 153.72 21.5246 144.953 14.5779 134.557C7.63117 124.16 3.02098 112.381 1.06496 100.032C-0.891055 87.6818 -0.146398 75.0547 3.24763 63.0204C6.64165 50.9861 12.6044 39.8307 20.7249 30.3227C28.8454 20.8148 38.9307 13.1804 50.2859 7.94562C61.6411 2.7108 73.9962 0 86.5 0V28.0211C78.0468 28.0211 69.694 29.8538 62.0173 33.3928C54.3405 36.9318 47.5223 42.0931 42.0323 48.521C36.5424 54.9489 32.5113 62.4906 30.2167 70.6265C27.9222 78.7623 27.4187 87.299 28.7411 95.6481C30.0635 103.997 33.1802 111.961 37.8766 118.989C42.573 126.018 48.7373 131.945 55.9449 136.361C63.1525 140.778 71.2319 143.58 79.6265 144.574C88.0212 145.567 96.5315 144.729 104.571 142.117L113.23 168.766Z" fill="#22C55E" />
    </svg>
  )
}

function getPreviousEpochMonth(epochMonth: number): number {
  return epochMonth - 1;
}

function getRoiValueColor(value: number, benchmark: number): string {
  if (value > 0 && benchmark > 0) {
    const roundedValue = Math.round(value * 10) / 10
    const roundedBenchmark = Math.round(benchmark * 10) / 10

    if (roundedValue < roundedBenchmark) return 'text-red-500'
    if (roundedValue > roundedBenchmark) return 'text-green-600'
  }

  return 'text-gray-900'
}

function RichText({ text }: { text: string }) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g)
  return (
    <>
      {parts.map((part, i) =>
        part.startsWith('**') && part.endsWith('**') ? (
          <strong key={i} className="font-semibold text-gray-900">
            {part.slice(2, -2)}
          </strong>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  )
}

export function HealthCashFlowSection({
  simpleHealthView = false,
}: {
  simpleHealthView?: boolean;
}) {
  const currentClientContext = CurrentClientProvider.useClient() as any
  const currentClient = currentClientContext?.client

  const [apiData, setApiData] = React.useState<HealthCashFlowApiData | null>(null)
  const [previousApiData, setPreviousApiData] = React.useState<HealthCashFlowApiData | null>(null)
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    let cancelled = false

    if (!currentClient?.Id) {
      setApiData(null)
      setPreviousApiData(null)
      setLoading(false)
      return () => { cancelled = true }
    }

    setLoading(true)

    const endEpoch = EpochMonthService.getTodayEpochMonth()
    const prevEndEpoch = getPreviousEpochMonth(endEpoch)

    Promise.all([
      ClientDataService.getHealthCashFlowSummary({
        clientId: currentClient.Id,
        endEpochMonth: endEpoch,
      }),
      ClientDataService.getHealthCashFlowSummary({
        clientId: currentClient.Id,
        endEpochMonth: prevEndEpoch,
      }),
    ])
      .then(([currentResponse, previousResponse]) => {
        if (cancelled) return
        setApiData(currentResponse?.data ?? null)
        setPreviousApiData(previousResponse?.data ?? null)
        setLoading(false)
      })
      .catch((error) => {
        if (cancelled) return
        console.error(error)
        setApiData(null)
        setPreviousApiData(null)
        setLoading(false)
      })

    return () => { cancelled = true }
  }, [currentClient?.Id])

  const summary = React.useMemo(() => deriveHealthCashFlowMetrics(apiData), [apiData])
  const previousSummary = React.useMemo(() => deriveHealthCashFlowMetrics(previousApiData), [previousApiData])

  const snapshot = React.useMemo(() => buildHealthSnapshot(summary), [summary])
  const previousSnapshot = React.useMemo(() => buildHealthSnapshot(previousSummary), [previousSummary])

  const [isEditingCashCard, setIsEditingCashCard] = React.useState(false)
  const [isScoreDrawerOpen, setIsScoreDrawerOpen] = React.useState(false)
  const [isVideoModalOpen, setIsVideoModalOpen] = React.useState(false)
  const [hoveredDonutSegment, setHoveredDonutSegment] = React.useState<'purchases' | 'expenses' | 'cashFlow' | null>(null)
  const [revenueInput, setRevenueInput] = React.useState('0')
  const [purchasesPctInput, setPurchasesPctInput] = React.useState('0.0')
  const [expensesPctInput, setExpensesPctInput] = React.useState('40.0')

  const scoreChange: number | null =
    snapshot && previousSnapshot ? snapshot.score - previousSnapshot.score : null

  const displayScore = loading ? 0 : snapshot?.score ?? 50

  const clientVertical = React.useMemo(() => resolveClientVertical(currentClient), [currentClient])
  const verticalBenchmark = VERTICAL_BENCHMARKS[clientVertical]
  const benchmarkCashMarginPct = verticalBenchmark.cashMarginPct
  const benchmarkPurchasesPct = Math.max(0, 100 - benchmarkCashMarginPct)
  const benchmarkExpensesPct = 40

  const [expandedMetric, setExpandedMetric] = React.useState<string | null>(null)

  const getInsightForRow = React.useCallback(
    (metricKey: 'salesGrowth' | 'turn' | 'markdowns' | 'freshness' | 'grossMargin' | 'cashMargin') => {
      if (!summary) return null
      const actualVal =
        metricKey === 'salesGrowth'
          ? percentageDelta(summary.sales, summary.salesComp)
          : metricKey === 'turn'
            ? summary.turn
            : metricKey === 'markdowns'
              ? summary.markdownPct * 100
              : metricKey === 'freshness'
                ? summary.freshness
                : metricKey === 'grossMargin'
                  ? summary.grossMarginPct
                  : summary.cashMarginPct

      const benchmarkVal =
        metricKey === 'salesGrowth'
          ? verticalBenchmark.salesYoYPct
          : metricKey === 'turn'
            ? verticalBenchmark.inventoryTurn
            : metricKey === 'markdowns'
              ? verticalBenchmark.markdownPct
              : metricKey === 'freshness'
                ? verticalBenchmark.freshnessPct
                : metricKey === 'grossMargin'
                  ? verticalBenchmark.grossMarginPct
                  : verticalBenchmark.cashMarginPct

      return computeMetricInsight({
        metricKey,
        actual: actualVal,
        benchmark: benchmarkVal,
        totalSales: summary.sales,
        currentInventory: summary.invAvgCost,
        currentCogs: summary.sales * (1 - summary.grossMarginPct / 100),
        totalInventoryValue: summary.invAvgCost,
      })
    },
    [summary, verticalBenchmark]
  )

  React.useEffect(() => {
    if (!summary) {
      setRevenueInput('0')
      setPurchasesPctInput('0.0')
      return
    }

    setRevenueInput(String(Math.round(summary.sales)))
    setPurchasesPctInput(summary.costOfPurchasesPct.toFixed(1))
  }, [summary?.sales, summary?.costOfPurchasesPct])

  const revenueDollars = Math.max(0, Number(revenueInput) || 0)
  const purchasesPct = Math.min(100, Math.max(0, Number(purchasesPctInput) || 0))
  const purchasesDollars = (purchasesPct / 100) * revenueDollars
  const cashMarginPct = Math.max(0, 100 - purchasesPct)
  const cashMarginDollars = (cashMarginPct / 100) * revenueDollars
  const expensesPct = Math.min(100, Math.max(0, Number(expensesPctInput) || 0))
  const expensesDollars = (expensesPct / 100) * revenueDollars
  const netCashFlowDollars = Math.max(0, cashMarginDollars - expensesDollars)
  const netCashFlowPct = Math.max(0, cashMarginPct - expensesPct)

  const purchasesDeltaVsBenchmark = purchasesPct - benchmarkPurchasesPct
  const cashMarginDeltaVsBenchmark = cashMarginPct - benchmarkCashMarginPct

  const donutHasData = purchasesPct > 0 || expensesPct > 0 || netCashFlowPct > 0
  const donutStops = donutHasData
    ? `#3B82F6 0 ${Math.max(0, purchasesPct)}%, #F59E0B ${Math.max(0, purchasesPct)}% ${Math.max(0, purchasesPct + expensesPct)}%, #22C55E ${Math.max(0, purchasesPct + expensesPct)}% 100%`
    : '#E5E7EB 0 100%'

  const activeDonutTooltip = React.useMemo(() => {
    if (!hoveredDonutSegment) return null
    if (hoveredDonutSegment === 'purchases') {
      return {
        title: 'Purchases',
        val: PerformanceDashboardService.moneyFormat(purchasesDollars, true),
        pct: purchasesPct,
        color: '#3B82F6',
      }
    }
    if (hoveredDonutSegment === 'expenses') {
      return {
        title: 'Expenses',
        val: PerformanceDashboardService.moneyFormat(expensesDollars, true),
        pct: expensesPct,
        color: '#F59E0B',
      }
    }
    return {
      title: 'Cash Flow',
      val: PerformanceDashboardService.moneyFormat(cashMarginDollars, true),
      pct: cashMarginPct,
      color: '#22C55E',
    }
  }, [hoveredDonutSegment, purchasesDollars, purchasesPct, expensesDollars, expensesPct, cashMarginDollars, cashMarginPct])

  const roiCards = React.useMemo(() => {
    if (!summary) return []

    const invAvgCost = summary.invAvgCost || 0
    const dynamicCmroi = invAvgCost ? cashMarginDollars / invAvgCost : 0
    const dynamicGrossMargin = revenueDollars * (summary.grossMarginPct / 100)
    const dynamicGmroi = invAvgCost ? dynamicGrossMargin / invAvgCost : 0
    const cmroiBenchmark = invAvgCost ? (revenueDollars * (benchmarkCashMarginPct / 100)) / invAvgCost : 0
    const gmroiBenchmark = invAvgCost ? (revenueDollars * (verticalBenchmark.grossMarginPct / 100)) / invAvgCost : 0

    return [
      {
        label: 'GMROI',
        value: dynamicGmroi,
        benchmark: gmroiBenchmark,
      },
      {
        label: 'CMROI',
        value: dynamicCmroi,
        benchmark: cmroiBenchmark,
      },
    ]
  }, [summary, cashMarginDollars, revenueDollars, benchmarkCashMarginPct, verticalBenchmark.grossMarginPct])

  // Simple view
  if (simpleHealthView) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-3 p-4">
        <div className="w-full max-w-[260px]">
          <ClientDashboardV3ScoreGauge className="w-full h-auto" score={displayScore} />
        </div>
        <div className="flex flex-col items-center -mt-2">
          <span className="text-5xl font-semibold leading-none tracking-tighter text-gray-900">
            {loading ? '—' : snapshot?.score ?? '—'}
          </span>
          <span className="text-sm text-gray-500">/100</span>
        </div>
      </div>
    )
  }

  // Full view
  return (
    <section className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="flex items-start justify-between gap-4 px-5 pb-0 pt-5">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1.5">
              <h3 className="text-sm font-semibold text-gray-900">RetailORBIT Health Score<sup className="text-[9px]">™</sup></h3>
              <KpiHelpIcon tooltipKey="retailOrbitHealthScore" />
            </div>
            <p className="max-w-[275px] text-xs leading-4 text-gray-500">The independent specialty retailer health score</p>
          </div>
        </div>

        <div className="flex flex-col gap-4 p-5 lg:flex-row">
          <div className="flex w-full flex-col gap-4 lg:w-[46%]">
            <div className="flex flex-col items-center gap-3 rounded-xl bg-slate-50 px-4 pb-4 pt-6">
              <ClientDashboardV3ScoreGauge className="w-full max-w-[220px]" score={displayScore} />
              <div className="-mt-7 flex flex-col items-center">
                <span className="text-[30px] font-semibold leading-none text-gray-900">{loading ? '—' : snapshot?.score ?? '—'}</span>
                <span className="text-xs text-gray-500">/100</span>
              </div>
              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-600">
                <History className="h-[11px] w-[11px]" />
                {scoreChange === null ? 'Score history unavailable' : `Score ${scoreChange >= 0 ? '+' : ''}${scoreChange} vs last month`}
              </span>
            </div>
          </div>

          <div className="flex w-full flex-col gap-3 lg:w-[54%] max-h-[390px] overflow-y-auto overflow-x-hidden pr-1.5">
            {loading ? (
              <div className="rounded-xl bg-gray-50 p-4 text-sm text-gray-500">Loading KPI-derived health signals…</div>
            ) : snapshot?.rows.map((row: HealthSnapshotRow) => {
              const isExpanded = expandedMetric === row.label
              const insight = getInsightForRow(row.metricKey)

              return (
                <div
                  key={row.label}
                  onClick={() => setExpandedMetric((prev) => (prev === row.label ? null : row.label))}
                  className="flex flex-col gap-2 rounded-xl p-2.5 transition-all cursor-pointer hover:bg-slate-50 border border-transparent hover:border-slate-200"
                >
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-800">
                      {row.label}
                      {resolveHealthRowTooltipKey(row.label) && (
                        <KpiHelpIcon tooltipKey={resolveHealthRowTooltipKey(row.label) as KpiTooltipKey} />
                      )}
                      {isExpanded ? (
                        <ChevronUp className="h-3.5 w-3.5 text-slate-400" />
                      ) : (
                        <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
                      )}
                    </span>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '3px 12px',
                        borderRadius: '0.625rem',
                        fontSize: '12px',
                        fontWeight: 700,
                        lineHeight: '18px',
                        backgroundColor: scoreBadgeStyles[row.tone]?.bg ?? '#E5E7EB',
                        color: scoreBadgeStyles[row.tone]?.text ?? '#374151',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row.badge}
                    </span>
                  </div>
                  <div className="flex gap-[5px] w-full">
                    {Array.from({ length: 5 }).map((_, index) => (
                      <div
                        key={`${row.label}-${index}`}
                        style={{
                          flex: 1,
                          height: '8px',
                          borderRadius: '4px',
                          backgroundColor: getSegmentColor(row.tone, index, row.filled),
                        }}
                      />
                    ))}
                  </div>
                  <p className="text-[11px] leading-[15px] text-slate-500 overflow-hidden text-ellipsis whitespace-nowrap">{row.caption}</p>

                  {isExpanded && insight && (
                    <div
                      style={{
                        backgroundColor: '#F8FAFC',
                        border: '1px solid #E2E8F0',
                        borderRadius: '8px',
                        padding: '14px 16px',
                        marginTop: '8px',
                      }}
                    >
                      <p style={{ margin: 0, fontSize: '13px', lineHeight: '20px', color: '#475569' }}>
                        <RichText text={insight.statement} />
                      </p>
                      {insight.tierLine && (
                        <p style={{ margin: '10px 0 0 0', fontSize: '12px', color: '#94A3B8', fontStyle: 'italic' }}>
                          {insight.tierLine}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
            {!loading && !snapshot && (
              <div className="rounded-xl bg-gray-50 p-4 text-sm text-gray-500">No KPI snapshot data is available for the current window.</div>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-2 px-5 pb-5 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            onClick={() => setIsScoreDrawerOpen(true)}
            className="text-[10px] font-medium text-purple-700 underline hover:text-purple-900 transition-colors cursor-pointer"
          >
            What does my score mean?
          </button>
          <span className="text-[10px] leading-[15px] text-gray-400">Last updated: May 06 2026</span>
        </div>
      </div>

      <div className="flex flex-col gap-5 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1.5">
              <h3 className="text-sm font-semibold text-gray-900">Cash Margin<sup className="text-[9px]">™</sup></h3>
              <KpiHelpIcon tooltipKey="cashMargin" />
            </div>
            <p className="text-xs text-gray-500">How your sales dollar flows</p>
          </div>
          {!loading && (
            <button
              type="button"
              onClick={() => setIsEditingCashCard((prev) => !prev)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3.5 py-1 text-xs font-semibold text-slate-700 shadow-2xs hover:bg-slate-50 transition-colors cursor-pointer"
            >
              <Pencil className="h-3.5 w-3.5 text-slate-600" />
              {isEditingCashCard ? 'Save' : 'Edit'}
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-[auto,1fr]">
          <div className="flex shrink-0 flex-col items-center gap-2">
            {loading ? (
              <div className="h-[156px] w-[156px] rounded-full border-[18px] border-gray-200" />
            ) : (
              <div
                className="relative h-[172px] w-[172px] cursor-pointer"
                onMouseLeave={() => setHoveredDonutSegment(null)}
                onMouseMove={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect()
                  const cx = rect.left + rect.width / 2
                  const cy = rect.top + rect.height / 2
                  const dx = e.clientX - cx
                  const dy = e.clientY - cy
                  let deg = (Math.atan2(dy, dx) * 180 / Math.PI + 90 + 360) % 360
                  const pDeg = (purchasesPct / 100) * 360
                  const eDeg = pDeg + (expensesPct / 100) * 360

                  if (deg <= pDeg) {
                    setHoveredDonutSegment('purchases')
                  } else if (deg <= eDeg) {
                    setHoveredDonutSegment('expenses')
                  } else {
                    setHoveredDonutSegment('cashFlow')
                  }
                }}
              >
                {activeDonutTooltip && (
                  <div className="absolute -top-12 left-1/2 -translate-x-1/2 z-30 pointer-events-none animate-in fade-in zoom-in-95 duration-150">
                    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-lg text-xs flex flex-col gap-0.5 whitespace-nowrap">
                      <span className="font-semibold text-slate-700">{activeDonutTooltip.title}</span>
                      <div className="flex items-center gap-1.5 font-bold text-slate-900">
                        <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: activeDonutTooltip.color }} />
                        <span>{activeDonutTooltip.val}</span>
                        <span className="font-normal text-slate-500">({activeDonutTooltip.pct.toFixed(0)}%)</span>
                      </div>
                    </div>
                  </div>
                )}

                <div
                  className="h-full w-full rounded-full transition-all"
                  style={{ background: `conic-gradient(${donutStops})` }}
                />
                <div className="absolute inset-[22px] flex flex-col items-center justify-center rounded-full bg-white text-center">
                  <span className="text-lg font-semibold text-gray-900">{PerformanceDashboardService.moneyFormat(cashMarginDollars, true)}</span>
                  <span className="text-[11px] text-gray-500">({ReportService.formatPercent(cashMarginPct)}%)</span>
                  <span className="text-[11px] tracking-wide text-gray-400">Cash Margin</span>
                </div>
              </div>
            )}
            <p className="text-center text-xs text-gray-500 mt-1">Est benchmark <span className="font-semibold text-gray-900">{benchmarkCashMarginPct.toFixed(1)}%</span></p>
            <button
              type="button"
              onClick={() => setIsVideoModalOpen(true)}
              className="mt-2.5 inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3.5 py-1 text-xs font-medium text-gray-700 shadow-2xs hover:bg-gray-50 transition-colors cursor-pointer"
            >
              <PlayCircle className="h-4 w-4 text-purple-600" />
              Watch Video
            </button>
          </div>

          <div className="flex flex-1 flex-col rounded-xl bg-gray-50 p-3.5">
            {loading ? (
              <div className="rounded-xl bg-white p-4 text-sm text-gray-500">Loading cash-flow metrics…</div>
            ) : (
              <>
                <div className="flex items-start justify-between gap-3 border-b border-gray-200 py-2.5">
                  <div className="flex items-center gap-2">
                    <span className="mt-0.5 h-2 w-2 shrink-0 rounded-full bg-gray-900" />
                    <span className="text-[13px] font-medium text-gray-800">Revenue</span>
                    <KpiHelpIcon tooltipKey="cashRevenue" />
                  </div>
                  {isEditingCashCard ? (
                    <div className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1">
                      <span className="text-[11px] text-gray-500">$</span>
                      <input
                        type="number"
                        min={0}
                        step={1000}
                        value={revenueInput}
                        onChange={(event) => setRevenueInput(event.target.value)}
                        className="w-24 border-0 bg-transparent text-right text-[13px] font-semibold text-gray-900 outline-none"
                      />
                    </div>
                  ) : (
                    <span className="text-[13px] font-semibold text-gray-900">{PerformanceDashboardService.moneyFormat(revenueDollars, true)}</span>
                  )}
                </div>

                <div className="flex items-start justify-between gap-3 border-b border-gray-200 py-2.5">
                  <div className="flex flex-col gap-0.5">
                    <div className="flex items-center gap-2">
                      <span className="mt-0.5 h-2 w-2 shrink-0 rounded-full bg-purple-500" />
                      <span className="text-[13px] text-gray-800">Purchases</span>
                      <KpiHelpIcon tooltipKey="cashPurchases" />
                    </div>
                    <span className="text-[11px] text-gray-500">Benchmark {benchmarkPurchasesPct.toFixed(1)}%</span>
                  </div>
                  <div className="flex flex-col items-end gap-0.5">
                    <div className="flex items-center gap-2">
                      <span className="text-[13px] font-medium text-gray-900">{PerformanceDashboardService.moneyFormat(purchasesDollars, true)}</span>
                      <DeltaBadge delta={purchasesDeltaVsBenchmark} invert />
                    </div>
                    {isEditingCashCard ? (
                      <div className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-0.5">
                        <input
                          type="number"
                          min={0}
                          max={100}
                          step={0.1}
                          value={purchasesPctInput}
                          onChange={(event) => setPurchasesPctInput(event.target.value)}
                          className="w-12 border-0 bg-transparent text-right text-[11px] text-gray-700 outline-none"
                        />
                        <span className="text-[11px] text-gray-500">%</span>
                      </div>
                    ) : (
                      <span className="text-[11px] text-gray-500">({ReportService.formatPercent(purchasesPct)})</span>
                    )}
                  </div>
                </div>

                <div className="flex items-start justify-between gap-3 border-b border-gray-200 py-2.5">
                  <div className="flex flex-col gap-0.5">
                    <div className="flex items-center gap-2">
                      <span className="mt-0.5 h-2 w-2 shrink-0 rounded-full bg-gray-900" />
                      <span className="text-[13px] text-gray-800">Cash Margin</span>
                      <KpiHelpIcon tooltipKey="cashMarginRow" />
                    </div>
                    <span className="text-[11px] text-gray-500">Benchmark {benchmarkCashMarginPct.toFixed(1)}%</span>
                  </div>
                  <div className="flex flex-col items-end gap-0.5">
                    <div className="flex items-center gap-2">
                      <span className="text-[13px] font-medium text-gray-900">{PerformanceDashboardService.moneyFormat(cashMarginDollars, true)}</span>
                      <DeltaBadge delta={cashMarginDeltaVsBenchmark} />
                    </div>
                    <span className="text-[11px] text-gray-500">({ReportService.formatPercent(cashMarginPct)})</span>
                  </div>
                </div>

                <div className="flex items-start justify-between gap-3 border-b border-gray-200 py-2.5">
                  <div className="flex flex-col gap-0.5">
                    <div className="flex items-center gap-2">
                      <span className="mt-0.5 h-2 w-2 shrink-0 rounded-full bg-amber-500" />
                      <span className="text-[13px] text-gray-800">Expenses</span>
                      <KpiHelpIcon tooltipKey="cashExpenses" />
                    </div>
                    <span className="text-[11px] text-gray-500">Est. benchmark {benchmarkExpensesPct.toFixed(1)}%</span>
                  </div>
                  <div className="flex flex-col items-end gap-0.5">
                    <span className="text-[13px] font-medium text-gray-900">{PerformanceDashboardService.moneyFormat(expensesDollars, true)}</span>
                    {isEditingCashCard ? (
                      <div className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-0.5">
                        <input
                          type="number"
                          min={0}
                          max={100}
                          step={0.1}
                          value={expensesPctInput}
                          onChange={(event) => setExpensesPctInput(event.target.value)}
                          className="w-12 border-0 bg-transparent text-right text-[11px] text-gray-700 outline-none"
                        />
                        <span className="text-[11px] text-gray-500">%</span>
                      </div>
                    ) : (
                      <span className="text-[11px] text-gray-500">({expensesPct.toFixed(1)}%)</span>
                    )}
                  </div>
                </div>

                <div className="flex items-start justify-between gap-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <span className="mt-0.5 h-2 w-2 shrink-0 rounded-full bg-emerald-500" />
                    <span className="text-[13px] font-medium text-gray-800">Net Cash Flow</span>
                    <KpiHelpIcon tooltipKey="netCashFlow" />
                  </div>
                  <div className="flex flex-col items-end gap-0.5">
                    <span className="text-[13px] font-semibold text-emerald-600">{PerformanceDashboardService.moneyFormat(netCashFlowDollars, true)}</span>
                    <span className="text-[11px] text-gray-500">({netCashFlowPct.toFixed(1)}%)</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {loading ? (
            <div className="col-span-2 rounded-xl bg-gray-50 p-4 text-sm text-gray-500">Loading ROI metrics…</div>
          ) : roiCards.map((card) => {
            const diff = card.value - card.benchmark
            return (
              <div key={card.label} className="flex flex-col gap-1 rounded-lg border border-gray-200 bg-white p-3">
                <div className="flex items-center gap-1">
                  <span className="text-xs font-semibold tracking-wide text-gray-500">{card.label}</span>
                  <HelpDot />
                </div>
                <span className="text-[28px] font-extrabold leading-none text-gray-900">
                  {card.value > 0 ? `${card.value.toFixed(1)}×` : '—'}
                </span>
                {card.benchmark > 0 && (
                  <div className="flex items-center gap-1.5 text-[11px] text-gray-400 mt-0.5">
                    <span>Benchmark {card.benchmark.toFixed(1)}</span>
                    {card.value > 0 && (
                      <span className={cn('font-medium text-[10px] inline-flex items-center gap-0.5', diff >= 0 ? 'text-emerald-600' : 'text-red-600')}>
                        {diff >= 0 ? '↗ +' : '↘ '}
                        {diff.toFixed(1)}
                      </span>
                    )}
                  </div>
                )}
              </div>
            )
          })}
          {!loading && roiCards.length === 0 && (
            <div className="col-span-2 rounded-xl bg-gray-50 p-4 text-sm text-gray-500">No ROI metrics are available for the current KPI window.</div>
          )}
        </div>
      </div>

      <HealthScoreDrawer
        isOpen={isScoreDrawerOpen}
        onClose={() => setIsScoreDrawerOpen(false)}
        score={displayScore}
        scoreChange={scoreChange}
        avgVerticalScore={64}
        lastUpdated="May 06, 2026"
      />

      <VideoPlayerModal
        isOpen={isVideoModalOpen}
        onClose={() => setIsVideoModalOpen(false)}
      />
    </section>
  )
}

